import axios from 'axios';
import debounce from 'lodash/debounce';
import type { ComputedRef, Ref } from 'vue';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { useToast } from '@rygen/rygen-ui/composables';
import { Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import { LocationSearchCriteria, LocationSearchResult } from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';

import { useSelectedDomain } from './useSelectedDomain';

export interface UseShippingPointsResponse {
  resetSearch: () => void;
  searchCriteria: Ref<LocationSearchCriteria>;
  searchResults: ComputedRef<Loadable<LocationSearchResult>>;
}

export const useShippingPoints = (): UseShippingPointsResponse => {
  useSelectedDomain(() => loadShippingPoints());
  const { t } = useI18n();
  const { owningClient } = useParentEntityStore();
  const toast = useToast();

  const searchCriteria: Ref<LocationSearchCriteria> = ref(
    new LocationSearchCriteria(),
  );

  const _searchResults: Ref<Loadable<LocationSearchResult>> = ref({
    loading: false,
  });

  const searchResults = computed(() => _searchResults.value);

  const loadShippingPoints = async () => {
    if (!authorization.hasAuthority('location', 'read')) {
      console.warn(
        'cannot loadShippingPoints, user is not authorized for "location:read"',
      );
      return;
    }

    if (!owningClient.value) {
      console.warn('cannot loadShippingPoints, no owning client selected');
      return;
    }

    try {
      _searchResults.value.loading = true;
      _searchResults.value.data = await api.shippingPoint.search(
        searchCriteria.value,
      );
    } catch (e) {
      if (axios.isCancel(e)) {
        return;
      }

      console.error('Error loading shipping points', e);
      _searchResults.value.error = e;
      toast.addError({
        detail: t('errors.loadingParam', {
          param: t('shippingPoints.shippingPoints', 2),
        }),
        error: e,
      });
    } finally {
      _searchResults.value.loading = false;
    }
  };
  const debouncedSearch = debounce(loadShippingPoints, 500);

  const resetSearch = () => {
    searchCriteria.value.page = 0;
    searchCriteria.value.criteria = '';
  };

  watch(searchCriteria, debouncedSearch, { deep: true });

  return {
    resetSearch,
    searchCriteria,
    searchResults,
  };
};
